import type { RulebookDisplayedSnapshot } from "@next-platform/client-common-core";
import { renderRulebookUserText, useTranslation } from "@next-platform/client-common-core";
import resolveHudMood from "@next-platform/client-common-core/rulebook/hud/resolveHudMood";
import { Drawer, Icon, IconSize, IconVariants, Position } from "@next-platform/client-common-spicy-ui";
import type { RulebookPublicView } from "@next-platform/packages-ts-types";
import { useTheme as useNextTheme } from "next-themes";
import React, { useEffect, useMemo } from "react";

import Trans from "@/components/Trans/Trans";
import { Theme } from "@/constants/theme";

import { HUD_ICON_BUTTON, HUD_LINE, HUD_SHEET_BG, HUD_TERTIARY } from "./hudClassNames";
import RulebookMeterPanelCard from "./RulebookMeterPanelCard";
import RulebookSheetFooter from "./RulebookSheetFooter";
import useSheetContentMounted from "./useSheetContentMounted";

type RulebookExplanationSheetProps = {
  rulebook: RulebookPublicView;
  snapshot: RulebookDisplayedSnapshot;
  characterName?: string;
  isMobile: boolean;
  open: boolean;
  /** Meter scrolled into view when the sheet opens. */
  focusMeterId?: string;
  handleClose: () => void;
  /** Whether the conversation's Rulebook evaluation is frozen. */
  isFrozen: boolean;
  /** False when there is no conversation yet — disables the settings controls. */
  settingsEnabled: boolean;
  onToggleFrozen: (frozen: boolean) => void;
  /** Hide meters: the panel leaves the screen while evaluation continues. */
  onToggleHidden: (isHidden: boolean) => void;
  "data-testid"?: string;
};

// Drawer's bottom sheet clamps to max-h-[85vh]; the inline maxHeight wins so the sheet reaches ~88vh.
const MOBILE_PANEL_STYLE = { height: "88vh", maxHeight: "88vh" } as const;
// Side drawers hardcode `w-[250px]`; an inline width is the only override that reliably wins.
const PANEL_WIDTH_PX = 400;
const DESKTOP_PANEL_STYLE = { width: PANEL_WIDTH_PX } as const;

/** "Beatrix \"Buzz\" Light" → "Beatrix": the sheet talks about the character by first name. */
const firstNameOf = (name?: string): string => (name ?? "").trim().split(/\s+/)[0] ?? "";

const RulebookExplanationSheet = ({
  rulebook,
  snapshot,
  characterName,
  isMobile,
  open,
  focusMeterId,
  handleClose,
  isFrozen,
  settingsEnabled,
  onToggleFrozen,
  onToggleHidden,
  "data-testid": testId = "RulebookExplanationSheet",
}: RulebookExplanationSheetProps) => {
  const { t } = useTranslation();
  const meters = rulebook.meters ?? [];
  const isContentMounted = useSheetContentMounted({ open });
  const firstName = firstNameOf(characterName);
  const drawerClassNames = useMemo(() => ({ drawer: `${HUD_SHEET_BG} ${isMobile ? "rounded-t-2xl" : ""}` }), [isMobile]);
  const titleValues = useMemo(() => ({ name: firstName }), [firstName]);
  const theme = useNextTheme().resolvedTheme === Theme.DARK ? "dark" : "light";
  const mood = useMemo(
    () => (isContentMounted ? resolveHudMood({ rulebook, snapshot, theme }) : null),
    [isContentMounted, rulebook, snapshot, theme],
  );
  const captionValues = useMemo(() => ({ caption: renderRulebookUserText({ text: mood?.caption ?? "", characterName }) }), [mood, characterName]);
  const moodDotStyle = useMemo(() => (mood ? { backgroundColor: mood.color } : undefined), [mood]);

  useEffect(() => {
    if (open && focusMeterId) {
      document.getElementById(`rulebook-panel-card-${focusMeterId}`)?.scrollIntoView({ block: "start" });
    }
  }, [open, focusMeterId]);

  return (
    <Drawer
      data-testid={testId}
      open={open}
      onOpenChange={handleClose}
      position={isMobile ? Position.BOTTOM : Position.RIGHT}
      classNames={drawerClassNames}
      style={isMobile ? MOBILE_PANEL_STYLE : DESKTOP_PANEL_STYLE}
    >
      <div className="flex h-full flex-col">
        <div className={`flex flex-none items-center gap-2.5 border-b px-[18px] py-4 ${HUD_LINE}`}>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[17px] font-bold tracking-[-0.01em]">
              <Trans i18nKey="chat:rulebook.hud.sheet.title" defaults={`How {{name}} responds to you`} values={titleValues} />
            </div>
            {mood ? (
              <div data-testid="RulebookExplanationSheet-Caption" className={`mt-[3px] flex items-center gap-1.5 text-xs ${HUD_TERTIARY}`}>
                <span data-testid="RulebookExplanationSheet-MoodDot" className="h-[7px] w-[7px] flex-none rounded-full" style={moodDotStyle} />
                <span className="min-w-0 truncate">
                  <Trans i18nKey="chat:rulebook.hud.sheet.caption" defaults={`Right now: {{caption}}`} values={captionValues} />
                </span>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            data-testid="RulebookExplanationSheet-CloseButton"
            aria-label={t("chat:rulebook.hud.sheet.close", { defaultValue: "Close" })}
            onClick={handleClose}
            className={`h-8 w-8 rounded-full ${HUD_ICON_BUTTON}`}
          >
            <Icon icon={IconVariants.CLOSE} size={IconSize.SMALL} />
          </button>
        </div>
        <div data-testid="RulebookExplanationSheet-Body" className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] pb-5 pt-4">
          {isContentMounted
            ? meters.map((meter) => (
                <div key={meter.id} id={`rulebook-panel-card-${meter.id}`}>
                  <RulebookMeterPanelCard meter={meter} snapshot={snapshot} characterName={characterName} />
                </div>
              ))
            : null}
          <RulebookSheetFooter
            rulebook={rulebook}
            characterFirstName={firstName}
            isFrozen={isFrozen}
            settingsEnabled={settingsEnabled}
            onToggleFrozen={onToggleFrozen}
            onToggleHidden={onToggleHidden}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default React.memo(RulebookExplanationSheet);
